import type {
    Breadcrumb,
    Context,
    Contexts,
    ErrorEvent,
    EventHint,
    Exception,
    StackFrame,
    Stacktrace,
    Thread,
} from '@sentry/vue';
import * as Sentry from '@sentry/vue';
import type { App } from 'vue';

type SentryMechanism = NonNullable<Exception['mechanism']>;

const REDACTED = '[redacted]';
const SAFE_ROUTE_NAME = /^[a-z0-9._:-]+$/i;
const EMAIL = /\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi;
const IBAN = /\b[A-Z]{2}\d{2}(?:[ ]?[A-Z0-9]){11,30}\b/gi;
const JWT = /\beyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\b/g;
const UUID = /\b[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}\b/gi;
const ULID = /\b[0-9A-HJKMNP-TV-Z]{26}\b/g;
const BEARER_TOKEN = /(bearer\s+)[A-Za-z0-9._~+/=-]+/gi;
const SENSITIVE_ASSIGNMENT =
    /\b(token|code|state|signature|verification_code|password|secret|authorization)=([^\s&,;]+)/gi;
const SAFE_ERROR_MESSAGES = [
    /^Cannot (?:read|set) properties of (?:undefined|null)(?: \(reading '[A-Za-z0-9_$]+'\))?$/,
    /^Cannot convert undefined or null to object$/,
    /^[A-Za-z_$][A-Za-z0-9_$]* is not defined$/,
    /^Cannot access '[A-Za-z_$][A-Za-z0-9_$]*' before initialization$/,
    /^Failed to fetch(?: \([A-Za-z0-9.-]+\))?$/,
    /^Load failed$/,
    /^Maximum call stack size exceeded$/,
    /^Script error\.?$/,
    /^Illegal invocation$/,
    /^Assignment to constant variable\.$/,
    /^ResizeObserver loop (?:completed with undelivered notifications|limit exceeded)\.?$/,
];

const SAFE_CONTEXT_FIELDS: Record<string, readonly string[]> = {
    app: ['app_name', 'app_version', 'app_identifier', 'build_type'],
    browser: ['name', 'version'],
    device: ['family', 'model', 'arch', 'device_type'],
    os: ['name', 'version', 'build'],
    runtime: ['name', 'version'],
    trace: ['trace_id', 'span_id', 'parent_span_id', 'op', 'status', 'origin'],
};

export const SENTRY_DATA_COLLECTION = {
    userInfo: false,
    cookies: false,
    httpHeaders: {
        request: false,
        response: false,
    },
    httpBodies: [],
    queryParams: false,
    genAI: {
        inputs: false,
        outputs: false,
    },
    stackFrameVariables: false,
    frameContextLines: 5,
};

const sanitizePathSegment = (segment: string): string => {
    if (
        /^\d+$/.test(segment) ||
        UUID.test(segment) ||
        ULID.test(segment) ||
        segment.includes('%') ||
        /^[A-Za-z0-9_-]{20,}$/.test(segment)
    ) {
        UUID.lastIndex = 0;
        ULID.lastIndex = 0;

        return REDACTED;
    }

    UUID.lastIndex = 0;
    ULID.lastIndex = 0;

    return segment;
};

const sanitizeUrl = (url: string): string => {
    const scheme = /^([a-z][a-z0-9+.-]*):/i.exec(url)?.[1]?.toLowerCase();
    if (scheme && !['http', 'https'].includes(scheme)) {
        return `${scheme}:${REDACTED}`;
    }

    const isAbsolute = /^[a-z][a-z0-9+.-]*:\/\//i.test(url);

    try {
        const parsed = new URL(url, 'https://milo.invalid');
        const path = parsed.pathname
            .replace(/\/(invoices|credit-notes|quotes)\/public\/[^/]+/gi, '/$1/public/[redacted]')
            .split('/')
            .map(sanitizePathSegment)
            .join('/');

        return isAbsolute ? `${parsed.origin}${path}` : path;
    } catch {
        return url
            .split(/[?#]/, 1)[0]
            .replace(/\/(invoices|credit-notes|quotes)\/public\/[^/]+/gi, '/$1/public/[redacted]');
    }
};

const sanitizeText = (value: string): string =>
    value
        .replace(/https?:\/\/[^\s)\]}>,]+/gi, sanitizeUrl)
        .replace(EMAIL, '[email]')
        .replace(IBAN, '[iban]')
        .replace(JWT, REDACTED)
        .replace(UUID, '[id]')
        .replace(ULID, '[id]')
        .replace(BEARER_TOKEN, `$1${REDACTED}`)
        .replace(SENSITIVE_ASSIGNMENT, `$1=${REDACTED}`);

// Our own error classes whose messages are constants in the code, never data.
const CONSTANT_MESSAGE_ERROR_TYPES = new Set(['TurnstileError']);

const sanitizeErrorMessage = (value: string, type?: string): string =>
    (type !== undefined && CONSTANT_MESSAGE_ERROR_TYPES.has(type)) ||
    SAFE_ERROR_MESSAGES.some((pattern) => pattern.test(value))
        ? sanitizeText(value)
        : '[message redacted]';

const pickContextFields = (context: Context, fields: readonly string[]): Context =>
    Object.fromEntries(fields.filter((field) => field in context).map((field) => [field, context[field]]));

const sanitizeContexts = (contexts: Contexts | undefined): Contexts | undefined => {
    if (!contexts) {
        return undefined;
    }

    const sanitized = Object.entries(SAFE_CONTEXT_FIELDS).reduce<Contexts>((result, [name, fields]) => {
        const context = contexts[name];
        if (context) {
            result[name] = pickContextFields(context, fields);
        }

        return result;
    }, {});

    return Object.keys(sanitized).length > 0 ? sanitized : undefined;
};

const sanitizeStackFrame = (frame: StackFrame): StackFrame => ({
    filename: frame.filename ? sanitizeUrl(frame.filename) : undefined,
    function: frame.function,
    module: frame.module,
    platform: frame.platform,
    lineno: frame.lineno,
    colno: frame.colno,
    abs_path: frame.abs_path ? sanitizeUrl(frame.abs_path) : undefined,
    context_line: frame.context_line,
    pre_context: frame.pre_context,
    post_context: frame.post_context,
    in_app: frame.in_app,
    instruction_addr: frame.instruction_addr,
    addr_mode: frame.addr_mode,
    debug_id: frame.debug_id,
});

const sanitizeStacktrace = (stacktrace: Stacktrace | undefined): Stacktrace | undefined =>
    stacktrace
        ? {
              frames: stacktrace.frames?.map(sanitizeStackFrame),
              frames_omitted: stacktrace.frames_omitted,
          }
        : undefined;

const sanitizeMechanism = (mechanism: SentryMechanism | undefined): SentryMechanism | undefined =>
    mechanism
        ? {
              type: mechanism.type,
              handled: mechanism.handled,
              synthetic: mechanism.synthetic,
              source: mechanism.source,
              is_exception_group: mechanism.is_exception_group,
              exception_id: mechanism.exception_id,
              parent_id: mechanism.parent_id,
          }
        : undefined;

const sanitizeException = (exception: Exception): Exception => ({
    type: exception.type,
    value: exception.value ? sanitizeErrorMessage(exception.value, exception.type) : undefined,
    mechanism: sanitizeMechanism(exception.mechanism),
    module: exception.module,
    thread_id: exception.thread_id,
    stacktrace: sanitizeStacktrace(exception.stacktrace),
});

const sanitizeThread = (thread: Thread): Thread => ({
    id: thread.id,
    main: thread.main,
    crashed: thread.crashed,
    current: thread.current,
    stacktrace: sanitizeStacktrace(thread.stacktrace),
});

export const sanitizeSentryBreadcrumb = (breadcrumb: Breadcrumb): Breadcrumb | null => {
    if (breadcrumb.category === 'console' || breadcrumb.category?.startsWith('console.')) {
        return null;
    }

    const sanitized: Breadcrumb = {
        type: breadcrumb.type,
        level: breadcrumb.level,
        event_id: breadcrumb.event_id,
        category: breadcrumb.category,
        timestamp: breadcrumb.timestamp,
    };

    if (['fetch', 'xhr', 'http'].includes(breadcrumb.category ?? '')) {
        const method = breadcrumb.data?.method;
        const statusCode = breadcrumb.data?.status_code;
        sanitized.data = {
            ...(typeof method === 'string' ? { method } : {}),
            ...(typeof statusCode === 'number' ? { status_code: statusCode } : {}),
        };
    }

    return sanitized;
};

const safeRouteName = (routeName: unknown): string | undefined =>
    typeof routeName === 'string' && SAFE_ROUTE_NAME.test(routeName) ? routeName : undefined;

export const sanitizeSentryEvent = (event: ErrorEvent, routeName?: unknown): ErrorEvent => {
    const route = safeRouteName(routeName);
    const breadcrumbs = event.breadcrumbs
        ?.map(sanitizeSentryBreadcrumb)
        .filter((breadcrumb): breadcrumb is Breadcrumb => breadcrumb !== null);

    return {
        type: undefined,
        event_id: event.event_id,
        message: event.message ? sanitizeErrorMessage(event.message) : undefined,
        logentry: event.logentry?.message
            ? {
                  message: sanitizeErrorMessage(event.logentry.message),
              }
            : undefined,
        timestamp: event.timestamp,
        level: event.level,
        platform: event.platform,
        logger: event.logger,
        release: event.release,
        dist: event.dist,
        environment: event.environment,
        sdk: event.sdk,
        request: event.request?.method ? { method: event.request.method } : undefined,
        transaction: route,
        modules: event.modules,
        exception: event.exception?.values ? { values: event.exception.values.map(sanitizeException) } : undefined,
        breadcrumbs,
        contexts: sanitizeContexts(event.contexts),
        tags: route ? { route } : undefined,
        debug_meta: event.debug_meta,
        threads: event.threads?.values ? { values: event.threads.values.map(sanitizeThread) } : undefined,
    };
};

const isAxiosError = (error: unknown): boolean =>
    error !== null && typeof error === 'object' && 'isAxiosError' in error;

export const createSentryBeforeSend =
    (getRouteName: () => unknown) =>
    (event: ErrorEvent, hint: EventHint): ErrorEvent | null => {
        if (isAxiosError(hint.originalException)) {
            return null;
        }

        // Attachments can contain screenshots, files or serialized state. Milo does
        // not need them for frontend error diagnosis, so fail closed here too.
        hint.attachments = [];

        return sanitizeSentryEvent(event, getRouteName());
    };

/** Report an error the UI already handled, so a recovered failure still shows up in Sentry. */
export const reportHandledError = (error: unknown): void => {
    Sentry.captureException(error);
};

export const initSentry = (app: App, getRouteName: () => unknown): void => {
    if (!import.meta.env.VITE_SENTRY_DSN || import.meta.env.NODE_ENV === 'development') {
        return;
    }

    Sentry.init({
        app,
        dsn: import.meta.env.VITE_SENTRY_DSN as string,
        // Staging and production share a project, so the build mode remains an
        // explicit technical dimension without exposing customer context.
        environment: import.meta.env.MODE,
        attachProps: false,
        dataCollection: SENTRY_DATA_COLLECTION,
        beforeBreadcrumb: sanitizeSentryBreadcrumb,
        beforeSend: createSentryBeforeSend(getRouteName),
    });
};
